import { Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';

import { AppoimentService } from '../appoinment-services/appoiment.service';
import {
  Question,
  Answer,
  QuestionSubmissionPayload,
  PatientDetails,
} from '../appoinment-const/appoinment-model';
import { DynamicQuestionComponent } from './components/dynamic-question/dynamic-question.component';
import { QuestionType } from '../appoinment-const/appoinment-enum';
import { FileUpload } from 'primeng/fileupload';
import { LocalStorageService } from 'projects/patient/src/app/core/auth/services/local-storage.service';
import { AuthService } from 'projects/patient/src/app/core/auth/services/auth.service';
import { PreScoreComponent } from '../pre-score/pre-score.component';
import { filter, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { PaymentSummuryComponent } from '../payment-summury/payment-summury.component';
import { Router } from '@angular/router';

@Component({
  selector: 'patient-pre-assesment',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    Button,
    ProgressSpinner,
    DynamicQuestionComponent,
    FileUpload,
    PaymentSummuryComponent,
    PreScoreComponent,
  ],
  templateUrl: './pre-assesment.component.html',
  styleUrl: './pre-assesment.component.css',
})
export class PreAssesmentComponent implements OnInit {
  // @Input()AppointmentDetails: any
  private fb = inject(FormBuilder);
  private appointmentService = inject(AppoimentService);
  localStorageService = inject(LocalStorageService);
  private router = inject(Router);
  authService = inject(AuthService);
  user: any = {};
  questionnaireId: string = '';
  AppointmentDetails: any = {};

  patientDetails: PatientDetails | null = null;

  form!: FormGroup;
  questions: Question[] = [];
  rootQuestions: Question[] = [];
  isLoading = true;
  isSubmitting = false;

  showScore: boolean = false;
  showAssessment: boolean = true;
  showSummury: boolean = false;
  patientID: string = '';
  pre_assessment_id: string = '';
  scoreDataInput: any;
  ngOnInit() {
    this.authService.getUserProfile().subscribe((res) => {
      ((this.user.patientID = res.PatientId),
        (this.user.userId = res.user_id),
        (this.user.userName = res.unique_name));
      if (this.user.patientID) {
        this.patientID = this.user.patientID;
        this.AppointmentDetails.patientID = this.patientID;
        this.AppointmentDetails.isFromAppointment = true;

        this.getPatientDetails();
        this.loadQuestions(this.user.patientID);
      }
    });
  }

  getPatientDetails() {
    this.appointmentService.getPatientData(this.patientID).subscribe({
      next: (res) => {
        this.patientDetails = res.data;
      },
      error: (err) => {
        console.error('Error loading patient details', err);
      },
    });
  }

  loadQuestions(id: string) {
    this.isLoading = true;
 
    this.appointmentService.getPreAssessmentQuestions(id, false).subscribe({
      next: (res) => {
        console.log(res);
 
        // Handle response based on your actual API structure
        const data = res?.questions || res || [];
        this.questionnaireId = data[0].questionnaire_id;
        this.questions = this.appointmentService.sortQuestions(data);
        this.rootQuestions = this.questions.filter((q) => !q.parent_id);
        this.initForm();
        this.isLoading = false;
        console.log(this.AppointmentDetails);
      },
      error: (err) => {
        console.error('Error loading questions', err);
        this.isLoading = false;
        // Mock data for development if API fails
      },
    });
  }

  initForm() {
    const group: any = {};
    this.questions.forEach((q) => {
      const validators = q.is_required ? [Validators.required] : [];

      // Multi-choice usually needs an array
      let defaultValue: any = null;
      if (q.question_type === QuestionType.Multi_Choice) {
        defaultValue = [];
      } else if (q.question_type === QuestionType.Progress_Bar) {
        defaultValue = 0;
      }

      group[q.id] = [defaultValue, validators];
    });
    // Add static health report control
    group['health_report'] = [null];
    this.form = this.fb.group(group);
  }

  onHealthReportUpload(event: any) {
    let files: File[] = [];

    if (event.files instanceof FileList) {
      files = Array.from(event.files);
    } else if (Array.isArray(event.files)) {
      files = event.files;
    } else {
      files = [event.files];
    }

    this.appointmentService.uploadHealthReport(this.patientID, files).subscribe({
      next: (res) => {
        this.form.patchValue({
          health_report: res.report_url || res.data?.report_url,
        });

        this.form.get('health_report')?.markAsDirty();
        this.form.get('health_report')?.markAsTouched();
      },
      error: (err) => console.error('Health report upload failed', err),
    });
  }

  get isFormValid(): boolean {
    if (!this.form) return false;

    // Only check validation for visible questions
    const visibleQuestionIds = this.getVisibleQuestionIds();
    return visibleQuestionIds.every((id) => {
      const control = this.form.get(id);
      return control ? control.valid : true;
    });
  }

  getVisibleQuestionIds(): string[] {
    const visibleIds: string[] = [];
    const traverse = (qs: Question[]) => {
      qs.forEach((q) => {
        visibleIds.push(q.id);

        // Find children of this question based on current value
        const control = this.form.get(q.id);
        const val = control?.value;
        if (!val) return;

        const optionsWithChildren = q.questionOptions.filter((opt) => opt.child_question_id);
        let activeChildIds: string[] = [];

        if (
          q.question_type === QuestionType.Single_Choice ||
          q.question_type === QuestionType.Yes_No
        ) {
          const selectedOption = optionsWithChildren.find((opt) => opt.value === val);
          if (selectedOption?.child_question_id)
            activeChildIds.push(selectedOption.child_question_id);
        } else if (q.question_type === QuestionType.Multi_Choice) {
          const selectedValues = Array.isArray(val) ? val : [];
          activeChildIds = optionsWithChildren
            .filter((opt) => selectedValues.includes(opt.value))
            .map((opt) => opt.child_question_id!)
            .filter((id) => !!id);
        }

        const children = this.questions.filter((child) => activeChildIds.includes(child.id));
        traverse(children);
      });
    };

    traverse(this.rootQuestions);
    return visibleIds;
  }

  onSubmit() {
    if (!this.isFormValid) return;

    this.isSubmitting = true;
    const visibleIds = this.getVisibleQuestionIds();
    const answers: Answer[] = visibleIds.map((id) => {
      const question = this.questions.find((q) => q.id === id)!;
      const value = this.form.get(id)?.value;

      let frontImg = null;
      let leftImg = null;
      let rightImg = null;
      let reportUrl = null;

      // Handle FullBodyImage specifically (Template 10)
      if (question.template_type === 10 && value && typeof value === 'object') {
        frontImg = value.front?.url || value.front?.name || null;
        leftImg = value.left?.url || value.left?.name || null;
        rightImg = value.right?.url || value.right?.name || null;
        reportUrl = value.report_url || null;
      }

      // Handle HealthReport specifically (Template 12)
      if (question.template_type === 12 && typeof value === 'string') {
        reportUrl = value;
      }

      this.AppointmentDetails.reportUrl = reportUrl;

      return {
        patient_Id: this.patientID, // Updated patient ID
        question_id: question.id,
        questionnaire_id: this.questionnaireId,
        questionnaire: 'Pre Assessment',
        question: question.question,
        question_type: question.question_type,
        question_values: Array.isArray(value)
          ? value.join(',')
          : typeof value === 'object'
            ? ''
            : value?.toString() || '',
        sequence: question.sequence,
        status: 1,
        front_profile_img: frontImg,
        left_profile_img: leftImg,
        right_profile_img: rightImg,
        report_url: reportUrl,
      };
    });

    const payload: QuestionSubmissionPayload = { answers };

    // Add health report if uploaded (as a special answer or however the backend expects)
    const healthReportUrl = this.form.get('health_report')?.value;
    if (healthReportUrl) {
      // Find the last answer and append the URL if the backend expects it there,
      // or add as a separate answer entry if needed.
      // User requested: "we pass this url in that sumint form apaylao of laste quatoion"
      if (answers.length > 0) {
        answers[answers.length - 1].report_url = healthReportUrl;
      }
    }

    console.log('Submitting Payload:', payload);

    // Extract BMI height and weight if present in answers to update vitals
    let bmiHeight: number | null = null;
    let bmiWeight: number | null = null;
    answers.forEach((ans) => {
      const question = this.questions.find((q) => q.id === ans.question_id);
      if (question && question.template_type === 11 && ans.question_values) {
        const match = ans.question_values.match(/height:\s*([\d.]+)\s*cm,\s*weight:\s*([\d.]+)\s*kg/i);
        if (match) {
          bmiHeight = parseFloat(match[1]);
          bmiWeight = parseFloat(match[2]);
        }
      }
    });

    this.appointmentService.submitPreAssessment(payload).subscribe({
      next: (res) => {
        console.log('Submission successful', res);
        this.isSubmitting = false;
        this.showScore = true;
        this.showAssessment = false;
        this.pre_assessment_id = res.pre_assessment_id;
        this.getAssessmentScore(this.pre_assessment_id);
        this.AppointmentDetails.assessmentID = this.pre_assessment_id;

        // Immediately update height/weight if they were entered
        if (bmiHeight && bmiWeight) {
          const vitalsPayload = {
            patient_id: this.patientID,
            height: bmiHeight,
            weight: bmiWeight
          };
          this.appointmentService.updateHeightWeight(this.patientID, vitalsPayload).subscribe({
            next: (vitalsRes) => console.log('Vitals updated successfully', vitalsRes),
            error: (vitalsErr) => console.error('Failed to update vitals', vitalsErr)
          });
        }

        // Wait 20 seconds and trigger addPatientActivityTrackingGoal API
        timer(20000).subscribe(() => {
          if (res.pre_assessment_id) {
            const goalPayload = {
              patient_id: this.patientID,
              pre_assessment_id: res.pre_assessment_id
            };
            this.appointmentService.addPatientActivityTrackingGoal(this.patientID, goalPayload).subscribe({
              next: (goalRes) => console.log('Activity goal added successfully', goalRes),
              error: (goalErr) => console.error('Failed to add activity goal', goalErr)
            });
          }
        });
      },
      error: (err) => {
        console.error('Submission failed', err);
        this.isSubmitting = false;
      },
    });
  }

  getAssessmentScore(id: string) {
    const timeout$ = timer(60000); // 1 minute timeout

    timer(0, 3000) // call immediately, then every 3 sec
      .pipe(
        takeUntil(timeout$),
        switchMap(() => this.appointmentService.getAssessmentScore(id, this.patientID)),
        tap((res: any) => console.log(res, 'score api')),
        filter((res: any) => res?.data?.json_data || res?.documents?.length > 0), // Wait until data exists
        take(1), // stop polling once data found
      )
      .subscribe({
        next: (res: any) => {
          if (res?.data?.json_data) {
            this.scoreDataInput = res; // Pass the whole response down
          } else if (res?.documents?.length > 0) {
            // Fallback for older structure
            this.scoreDataInput = res.documents;
          }
          console.log('scoreDataInput in pre-assessment component :', this.scoreDataInput);
        },
        error: (err) => {
          console.warn(err);
        },
        complete: () => {
          // If polling completes due to the 1-minute timeout without getting data, bypass directly to payment summary
          if (
            !this.scoreDataInput ||
            (Array.isArray(this.scoreDataInput) && this.scoreDataInput.length === 0)
          ) {
            console.log(
              'Assessment score API timed out after 1 minute. Moving directly to payment summary.',
            );
            this.handleProceed(true);
          }
        },
      });
  }

  handleProceed(value: boolean) {
    this.showSummury = value;
    this.showScore = false;
  }

  goToScore() {
    this.router.navigateByUrl('create-score', {
      state: {
        assessmentDetails: this.AppointmentDetails,
      },
    });
    this.showScore = true;
    this.showAssessment = false;
  }
}
